<script>
import { WarrantyOrderDocumentMixin } from '@/components/service/WarrantyOrderDocument'
import { DateTime } from 'luxon'

export default {
  mixins: [WarrantyOrderDocumentMixin],

  data() {
    return {
      form: {
        orderId: '',
        date: '',
        damageCausingPart: '',
        makerCode: '',
        damageLocations: [],
        tpi: '',
        damageTypes: [],
        documents: [],
        miscellaneousDocument: '',
        breakdown: [],
        dateSignatureEmployee: '',
        dateSignatureMechanic: '',
        internalComment: ''
      },
      damageLocations: [
        { id: 'right', label: 'rechts' },
        { id: 'left', label: 'links' },
        { id: 'front', label: 'vorne' },
        { id: 'back', label: 'hinten' },
        { id: 'twoDoors', label: '2-türig' },
        { id: 'fourDoors', label: '4-türig' }
      ],
      damageTypes: [
        { id: 10, label: 'Mechanischer Fehler' },
        { id: 21, label: 'Crimp am Kontakt' },
        { id: 31, label: 'Verfärbt' },
        { id: 11, label: 'Einstellfehler' },
        { id: 22, label: 'Kontakt/Stecker verpolt' },
        { id: 32, label: 'Wellig, verformt' },
        { id: 12, label: 'Falsches Serienteil' },
        { id: 23, label: 'Kontakt/Stecker lose' },
        { id: 33, label: 'Korrodiert' },
        { id: 13, label: 'Unrund, Unwucht' },
        { id: 24, label: 'Leitung eingeklemmt' },
        { id: 38, label: 'Fehlendes Serienteil' },
        { id: 14, label: 'Verbrannt, rutscht' },
        { id: 25, label: 'Leitung angescheuert' },
        { id: 39, label: 'Softwarefehler' },
        { id: 15, label: 'Gerissen, gebrochen' },
        { id: 26, label: 'Leitung lose, abgefallen' },
        { id: 40, label: 'Elektrische Fehler' },
        { id: 16, label: 'Unwirksam' },
        { id: 27, label: 'elektr. Schraubverbindung lose' },
        { id: 49, label: 'Störender Geruch' },
        { id: 17, label: 'Schwergängig' },
        { id: 28, label: 'Steckgehäuse/Steckkupplung' },
        { id: 50, label: 'Undicht' },
        { id: 18, label: 'Leichtgängig, lose' },
        { id: 29, label: 'Leitungsverlegung' },
        { id: 51, label: 'Windgeräusche' },
        { id: 20, label: 'Laut' },
        { id: 30, label: 'Optische Schäden' }
      ]
    }
  },

  created() {
    this.$set(this.form, 'dateSignatureEmployee', DateTime.local().toFormat('dd.MM.yyyy'))
    this.$set(this.form, 'dateSignatureMechanic', DateTime.local().toFormat('dd.MM.yyyy'))
    this.$set(this.form, 'date', this.today)
  },

  methods: {
    onOrderPanelExpanded() {
      this.focusInputRef('orderIdInputRef')
    },

    onDamageCausingPartReleasePanelExpanded() {
      this.focusInputRef('dateSignatureEmployeeInputRef')
    },

    getSubmittableData() {
      return {
        ...this.form,
        breakdown: [...this.form.breakdown].filter(item => !!item.description || !!item.mechanicId || !!item.timeTracking),
        tpi: this.form.damageTypes.includes('tpi') ? this.form.tpi : '',
        miscellaneousDocument: this.form.documents.includes('miscellaneous') ? this.form.miscellaneousDocument : ''
      }
    }
  }
}
</script>

<template>
  <div>
    <mia-expandable-panel
      title="Auftragsdaten"
      is-expanded
      @expanded="onOrderPanelExpanded"
    >
      <div class="grid grid-cols-12 gap-4 p-4">
        <!-- orderId -->
        <div class="col-span-3 flex items-center">
          <label
            for="orderId"
            class="tracking-wider text-gray-800"
          >
            Auftragsnummer
          </label>
        </div>
        <div class="col-span-3">
          <mia-feature-input
            id="orderId"
            ref="orderIdInputRef"
            v-model="form.orderId"
          />
        </div>

        <!-- date -->
        <div class="col-span-3 flex items-center justify-end">
          <label
            for="date"
            class="tracking-wider text-gray-800"
          >
            Datum
          </label>
        </div>
        <div class="col-span-3">
          <mia-feature-input
            id="date"
            v-model="form.date"
            type="date"
          />
        </div>

        <!-- damageCausingPart -->
        <div class="col-span-3 flex items-center">
          <label
            for="damageCausingPart"
            class="tracking-wider text-gray-800"
          >
            Schadensverursachendes Teil
          </label>
        </div>
        <div class="col-span-3">
          <mia-feature-input
            id="damageCausingPart"
            v-model="form.damageCausingPart"
          />
        </div>

        <!-- makerCode -->
        <div class="col-span-3 flex items-center justify-end">
          <label
            for="makerCode"
            class="tracking-wider text-gray-800"
          >
            Herstellercode 3-stellig
          </label>
        </div>
        <div class="col-span-3">
          <mia-feature-input
            id="makerCode"
            v-model="form.makerCode"
          />
        </div>
      </div>
    </mia-expandable-panel>

    <mia-expandable-panel
      title="Schadensort"
      is-expanded
      class="mt-4"
    >
      <div class="p-4">
        <div class="grid grid-cols-4 gap-4">
          <label
            v-for="damageLocation in damageLocations"
            :key="damageLocation.id"
            class="flex items-center font-normal"
          >
            <mia-checkbox
              :checked="form.damageLocations.includes(damageLocation.id)"
              class="w-5 h-5"
              @change="onDamageLocationChange(damageLocation, $event)"
            />
            <span class="ml-2">{{ damageLocation.label }}</span>
          </label>
        </div>
      </div>
    </mia-expandable-panel>

    <mia-expandable-panel
      title="Fehlerfeststellung am Schaden verursachenden Bauteil aus technischer Sicht"
      is-expanded
      class="mt-4"
    >
      <div class="p-4 grid grid-cols-3 gap-x-4 gap-y-2">
        <label
          v-for="damageType in damageTypes"
          :key="damageType.id"
          class="flex items-center font-normal"
        >
          <mia-checkbox
            :checked="form.damageTypes.includes(damageType.id)"
            class="w-5 h-5"
            @change="onDamageTypeChange(damageType, $event)"
          />
          <span class="ml-2">{{ damageType.id }} {{ damageType.label }}</span>
        </label>

        <div class="flex items-center">
          <label class="flex items-center font-normal">
            <mia-checkbox
              :checked="form.damageTypes.includes('tpi')"
              class="w-5 h-5"
              @change="onDamageTypeChange({ id: 'tpi' }, $event)"
            />
            <span class="ml-2">nach TPI</span>
          </label>
          <div class="mx-2 flex items-center">
            <mia-feature-input
              v-model="form.tpi"
              :disabled="!form.damageTypes.includes('tpi')"
            />
          </div>
          <div class="flex items-center">
            abgearbeitet
          </div>
        </div>
      </div>
    </mia-expandable-panel>

    <mia-expandable-panel
      title="Zusätzliche Unterlagen"
      is-expanded
      class="mt-4"
    >
      <div class="p-4 grid grid-cols-3 gap-x-4 gap-y-2">
        <label class="relative pl-8 font-normal">
          <mia-checkbox
            :checked="form.documents.includes('diagnosticProtocol')"
            class="w-5 h-5 absolute left-0 top-0"
            @change="onDocumentChange('diagnosticProtocol', $event)"
          />
          <div>
            Diagnoseprotokoll
          </div>
          <div class="text-xs text-gray-800">
            (Onlineversendung notwendig!)
          </div>
        </label>

        <label class="relative pl-8 font-normal">
          <mia-checkbox
            :checked="form.documents.includes('compressionCheck')"
            class="w-5 h-5 absolute left-0 top-0"
            @change="onDocumentChange('compressionCheck', $event)"
          />
          <div>
            Kompressionsprüfung
          </div>
        </label>

        <label class="relative pl-8 font-normal">
          <mia-checkbox
            :checked="form.documents.includes('climateControlProtocol')"
            class="w-5 h-5 absolute left-0 top-0"
            @change="onDocumentChange('climateControlProtocol', $event)"
          />
          <div>
            Klimaanlagenprotokoll
          </div>
        </label>

        <label class="relative pl-8 font-normal">
          <mia-checkbox
            :checked="form.documents.includes('measurementProtocolsBeforeAfter')"
            class="w-5 h-5 absolute left-0 top-0"
            @change="onDocumentChange('measurementProtocolsBeforeAfter', $event)"
          />
          <div>
            Vermessungsprotokolle
          </div>
          <div class="text-xs text-gray-800">
            (vorher / nachher)
          </div>
        </label>

        <label class="relative pl-8 font-normal">
          <mia-checkbox
            :checked="form.documents.includes('pictures')"
            class="w-5 h-5 absolute left-0 top-0"
            @change="onDocumentChange('pictures', $event)"
          />
          <div>
            Bilder
          </div>
        </label>

        <label class="relative pl-8 font-normal">
          <mia-checkbox
            :checked="form.documents.includes('audio')"
            class="w-5 h-5 absolute left-0 top-0"
            @change="onDocumentChange('audio', $event)"
          />
          <div>
            Audiodatei
          </div>
        </label>

        <label class="relative pl-8 font-normal">
          <mia-checkbox
            :checked="form.documents.includes('batteryCheckProtocol')"
            class="w-5 h-5 absolute left-0 top-0"
            @change="onDocumentChange('batteryCheckProtocol', $event)"
          />
          <div>
            Batterieprüfprotokoll
          </div>
          <div class="text-xs text-gray-800">
            (Test im Garantiemodus?)
          </div>
        </label>

        <div class="col-span-2 flex">
          <div>
            <label class="relative pl-8 font-normal">
              <mia-checkbox
                :checked="form.documents.includes('miscellaneous')"
                class="w-5 h-5 absolute left-0 top-0"
                @change="onDocumentChange('miscellaneous', $event)"
              />
              <div>
                Sonstiges:
              </div>
            </label>
          </div>
          <div class="flex-1 pl-2">
            <mia-feature-textarea
              v-model="form.miscellaneousDocument"
              :disabled="!form.documents.includes('miscellaneous')"
            />
          </div>
        </div>
      </div>
    </mia-expandable-panel>

    <mia-expandable-panel
      title="Durchgeführte Arbeiten"
      is-expanded
      class="mt-4"
      @expanded="onBreakdownPanelExpanded"
    >
      <table class="w-full">
        <thead>
          <tr>
            <th class="p-2">
              <div>Genaue Auflistung aller durchgeführten Arbeiten</div>
              <div class="text-xs text-gray-800">
                (inkl. Prüf- und Messarbeiten)
              </div>
            </th>
            <th class="p-2">
              Mechaniker
            </th>
            <th class="p-2">
              ZE
            </th>
            <th class="p-2">
&nbsp;
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in form.breakdown"
            :key="item.id"
          >
            <td class="p-2">
              <mia-feature-input
                :ref="`breakdownDescriptionRef${item.id}`"
                :value="item.description"
                @input="onBreakdownDescriptionInput(item, $event)"
              />
            </td>
            <td class="p-2">
              <mia-feature-input
                :value="item.mechanicId"
                @input="onBreakdownMechanicIdInput(item, $event)"
              />
            </td>
            <td class="p-2">
              <mia-feature-input
                :value="item.timeTracking"
                @input="onBreakdownTimeTrackingInput(item, $event)"
                @blur="onBreakdownTimeTrackingBlur(item, $event)"
              />
            </td>
            <td class="p-2">
              <button
                type="button"
                class="w-8 h-8 rounded inline-flex items-center justify-center hover:bg-red-500 hover:text-white focus:outline-none"
                @click="removeBreakdownItem(item)"
              >
                <mia-streamline-icon
                  class="w-5 h-5 fill-current"
                  src="bin-1"
                />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </mia-expandable-panel>

    <mia-expandable-panel
      title="Abgabe des Schadensverursachenden Teiles"
      is-expanded
      class="mt-4"
      @expanded="onDamageCausingPartReleasePanelExpanded"
    >
      <div class="p-4 grid grid-cols-2 gap-4">
        <mia-feature-input
          ref="dateSignatureEmployeeInputRef"
          v-model="form.dateSignatureEmployee"
          label="Datum, Unterschrift Mitarbeiter"
        />
        <mia-feature-input
          v-model="form.dateSignatureMechanic"
          label="Datum, Unterschrift Monteur"
          class="mt-4"
        />
      </div>
    </mia-expandable-panel>

    <mia-expandable-panel
      title="Intern"
      is-expanded
      class="mt-4"
      @expanded="onInternalPanelExpanded"
    >
      <div class="p-4">
        <mia-feature-textarea
          ref="internalCommentInputRef"
          v-model="form.internalComment"
          label="Kommentar (intern)"
        />
      </div>
    </mia-expandable-panel>
  </div>
</template>
